import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getAllCompaniesPanels } from '../../api/companyApi';
import { listStudents } from '../../api/authApi';
import { exportPlacementData } from '../../api/preferenceApi';
import { toast } from 'react-toastify';
import UploadStudentPreferencesModal from '../../components/admin/UploadStudentPreferencesModal';

const AdminDashboard = () => {
  const [companies, setCompanies] = useState([]);
  const [students, setStudents] = useState([]);
  const [stats, setStats] = useState({
    totalCompanies: 0,
    totalStudents: 0,
    registeredStudents: 0,
    studentsWithAccess: 0,
    studentsWithPreferences: 0,
    shortlistedStudents: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch companies and students in parallel
        const [companiesData, studentsData] = await Promise.all([
          getAllCompaniesPanels(),
          listStudents()
        ]);
        
        setCompanies(companiesData || []);
        setStudents(studentsData || []);
        
        // Calculate stats
        const registeredStudents = studentsData.filter(s => s.registration_complete).length;
        const studentsWithAccess = studentsData.filter(s => s.can_access_portal).length;
        const studentsWithPreferences = studentsData.filter(s => s.company_preferences?.length > 0).length;
        const shortlistedStudents = studentsData.filter(s => 
          s.company_preferences?.some(p => p.is_shortlisted)
        ).length;
        
        setStats({
          totalCompanies: companiesData.length,
          totalStudents: studentsData.length,
          registeredStudents,
          studentsWithAccess,
          studentsWithPreferences,
          shortlistedStudents
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('Failed to fetch dashboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const handleExportData = async () => {
    try {
      setExporting(true);
      await exportPlacementData();
      toast.success('Placement data exported successfully!');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export placement data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Container className="py-4">
      <h1 className="mb-4">Placement Unit Admin Dashboard</h1>
      
      {error && <Alert variant="danger">{error}</Alert>}
      
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading dashboard data...</p>
        </div>
      ) : (
        <>
          <Row className="mb-4">
            <Col md={4}>
              <Card className="bg-primary text-white h-100">
                <Card.Body className="d-flex flex-column">
                  <Card.Title>Companies</Card.Title>
                  <h2 className="mb-4">{stats.totalCompanies}</h2>
                  <p className="mb-4">Total companies registered in the system</p>
                  <div className="mt-auto">
                    <Link to="/admin/companies" className="btn btn-light btn-sm">
                      Manage Companies
                    </Link>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            
            <Col md={4}>
              <Card className="bg-success text-white h-100">
                <Card.Body className="d-flex flex-column">
                  <Card.Title>Students</Card.Title>
                  <h2 className="mb-4">{stats.totalStudents}</h2>
                  <p className="mb-4">
                    {stats.registeredStudents} registered, {stats.studentsWithAccess} with portal access
                  </p>
                  <div className="mt-auto">
                    <Link to="/admin/students" className="btn btn-light btn-sm">
                      Manage Students
                    </Link>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            
            <Col md={4}>
              <Card className="bg-info text-white h-100">
                <Card.Body className="d-flex flex-column">
                  <Card.Title>Preferences & Shortlists</Card.Title>
                  <h2 className="mb-4">{stats.studentsWithPreferences}</h2>
                  <p className="mb-4">
                    Students with preferences, {stats.shortlistedStudents} shortlisted
                  </p>
                  <div className="mt-auto">
                    <Link to="/admin/students" className="btn btn-light btn-sm">
                      View Details
                    </Link>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          
          <Row>
            <Col md={6}>
              <Card className="mb-4">
                <Card.Header as="h5">Quick Actions</Card.Header>
                <Card.Body>
                  <div className="d-grid gap-3">
                    <Link to="/admin/companies" className="btn btn-outline-primary">
                      Upload Company Data
                    </Link>
                    <Link to="/admin/students" className="btn btn-outline-success">
                      Manage Portal Access
                    </Link>
                    <Link to="/admin/students" className="btn btn-outline-warning">
                      Set Company Shortlists
                    </Link>
                    <Button 
                      variant="outline-info" 
                      onClick={() => setShowUploadModal(true)}
                    >
                      Upload Student & Preferences
                    </Button>
                    <Link to="/admin/global-scheduling" className="btn btn-outline-danger">
                      Schedule Interviews
                    </Link>
                    <Button 
                      variant="outline-secondary" 
                      onClick={handleExportData}
                      disabled={exporting}
                    >
                      {exporting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Exporting...
                        </>
                      ) : (
                        'Export Placement Data to Excel'
                      )}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            
            <Col md={6}>
              <Card className="mb-4">
                <Card.Header as="h5">Recent Companies</Card.Header>
                <Card.Body>
                  {companies.length === 0 ? (
                    <Alert variant="info">No companies added yet.</Alert>
                  ) : (
                    <ul className="list-group">
                      {companies.slice(0, 5).map(company => (
                        <li key={company.id} className="list-group-item d-flex justify-content-between align-items-center">
                          <div>
                            <strong>{company.name}</strong>
                            <div className="text-muted small">
                              Visit Date: {new Date(company.visit_date).toLocaleDateString()}
                            </div>
                          </div>
                          <span className="badge bg-primary rounded-pill">
                            {company.rounds?.length || 0} Rounds
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  
                  <div className="mt-3 text-end">
                    <Link to="/admin/companies" className="btn btn-sm btn-primary">
                      View All
                    </Link>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
      
      <UploadStudentPreferencesModal
        show={showUploadModal}
        onHide={() => setShowUploadModal(false)}
        onSuccess={() => {
          setShowUploadModal(false);
          // Refresh data after successful upload
          window.location.reload();
        }}
      />
    </Container>
  );
};

export default AdminDashboard;